import React from 'react';
import styles from './Meetings.module.scss';
import { useParams } from 'react-router-dom';
import useApiData from '../../../../../../hooks/useApiData';
import Loading from '../../../../Common/Loading/Loading';
import { getErrorComponentFromHttpError } from '../../../../Common/Error/Error';
import { getAbsoluteTimestamp } from '../../../../../../util/formatDate';
import Table, { MEETING_STATUS } from '../../../../Common/Table/Table/Table';
import NextMeetingBanner from './NextMeetingBanner/NextMeetingBanner';
import useAuth from '../../../../../../hooks/useAuth';

function getSortOrderFromStatus(status) {
  switch (status) {
    case 'SCHEDULED':
      return 4;
    case 'UPCOMING':
      return 3;
    case 'OVERDUE':
      return 2;
    case 'IN_PROGRESS':
      return 1;
    default:
      return 10;
  }
}

function meetingSortFn(a, b) {
  const statusA = getSortOrderFromStatus(a);
  const statusB = getSortOrderFromStatus(b);

  if (statusA < statusB) {
    return -1;
  }
  if (statusA > statusB) {
    return 1;
  }
  if (a.scheduledTime < b.scheduledTime) {
    return -1;
  }
  if (a.scheduledTime < b.scheduledTime) {
    return 1;
  }
  return 0;
}

export default function Meetings() {
  const { currentUser } = useAuth();
  const { teamId } = useParams();
  const isLead = currentUser.teams.filter((t) => t.id === teamId)?.[0]?.isLead;
  const { data, error, isLoading } = useApiData({
    path: `/user/teams/${teamId}/meetings`,
  });

  if (isLoading) {
    return <Loading message="Fetching meetings..." />;
  }

  if (error || data === null) {
    return getErrorComponentFromHttpError(error);
  }

  const formattedData = data.map((m) => ({
    ...m,
    startingWeek: getAbsoluteTimestamp(new Date(m.minTime)),
    attended: ['PRESENT', 'JOINED'].includes(m.attendance),
    meetingTime: m.startTime || m.scheduledTime,
  }));

  const nextMeetings = data.filter((m) =>
    ['SCHEDULED', 'UPCOMING', 'IN_PROGRESS', 'OVERDUE'].includes(m.status),
  );
  nextMeetings.sort(meetingSortFn);

  const additionalTeamLeadProps =
    (isLead && {
      linkFunction: (id) => `${id}/details`,
    }) ||
    {};

  return (
    <div className={styles.meetings}>
      {nextMeetings.slice(0, 1).map((m) => (
        <NextMeetingBanner key={`${m.id}-nextMeetingBanner`} meeting={m} />
      ))}
      <Table
        rows={formattedData}
        title="Meetings"
        allowSort
        defaultSortKey="meetingTime"
        defaultSortOrder="desc"
        allowPagination
        pageSize={10}
        {...additionalTeamLeadProps}
        columns={[
          {
            id: 'meetingTime',
            label: 'Meeting time',
            date: true,
          },
          {
            id: 'attended',
            label: 'Attended',
            boolean: true,
          },
          {
            id: 'startingWeek',
            label: 'Starting week',
          },
          {
            id: 'status',
            label: 'Status',
            enum: MEETING_STATUS,
          },
        ]}
      />
    </div>
  );
}
